import { useRef, useState } from "react";
import { useNotification } from "@context/NotificationContext";
import { useAuthStore } from "@persisted-state/auth/auth.store";
import {
  drybulkCargoOperationsService,
  type MessageFile,
  type StagedAttachment,
} from "@services/drybulkCargoOperationsApi";
import { sendMessage } from "@services/messageApi";
import { useQueryClient } from "@tanstack/react-query";
import { runTransaction } from "@telemetry/runTransaction";
import { useTranslation } from "react-i18next";
import {
  DRYBULK_CARGO_OPS_LANG_BASE,
  DRYBULK_CARGO_OPS_QUERY_KEYS,
  DRYBULK_CARGO_OPS_TELEMETRY,
  MESSAGE_ENTITY_TYPE,
} from "../constants";
import {
  messageErrorText,
  messageNotificationTypes,
  type MessageRole,
} from "../utils/messageThread";
import { problemMessage, problemStatus } from "../utils/problemDetail";
import { messageDraftKey, useMessageDraftStore } from "./useMessageDraftStore";

const L = `${DRYBULK_CARGO_OPS_LANG_BASE}.detail.messages`;
const NO_STAGED: StagedAttachment[] = [];

const toMessageFile = (staged: StagedAttachment): MessageFile => ({
  documentUrl: staged.documentUrl,
  originalFileName: staged.originalFileName,
  contentType: staged.contentType,
  fileSizeBytes: staged.fileSizeBytes,
  documentStatus: staged.documentStatus,
});

interface SendTarget {
  operationId: string;
  threadKey: string | undefined;
  role: MessageRole | null;
  vesselCallId: string | null | undefined;
}

export const useSendCargoMessage = ({ operationId, threadKey, role, vesselCallId }: SendTarget) => {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const queryClient = useQueryClient();
  const user = useAuthStore((state) => state.user);
  const store = useMessageDraftStore;
  const key = messageDraftKey(operationId, threadKey ?? "");
  const draft = useMessageDraftStore((state) => state.drafts[key]);
  const pendingAttach = draft?.pendingAttach;
  const heldFiles = useRef(new Map<string, File>());
  const [sending, setSending] = useState(false);
  const [staging, setStaging] = useState(0);
  const [retryingId, setRetryingId] = useState<string | null>(null);
  const [inlineError, setInlineError] = useState<string | null>(null);

  const text = draft?.text ?? "";
  const staged = draft?.staged ?? NO_STAGED;
  const busy = sending || staging > 0 || retryingId !== null;
  const canSend = !busy && Boolean(threadKey) && (text.trim() !== "" || staged.length > 0);

  const toast = (description: string) =>
    notify({ type: "error", title: t("common.error"), description });

  const telemetry = (action: string) => ({
    module: DRYBULK_CARGO_OPS_TELEMETRY.module,
    screen: DRYBULK_CARGO_OPS_TELEMETRY.messagesScreen,
    action,
    entityType: "cargo_operation",
    entityId: operationId,
  });

  const stageFiles = async (files: File[]): Promise<StagedAttachment[]> => {
    setStaging((n) => n + files.length);
    const done: StagedAttachment[] = [];
    await Promise.all(
      files.map(async (file) => {
        try {
          const result = await drybulkCargoOperationsService.stageAttachment(file, "");
          heldFiles.current.set(result.documentUrl, file);
          done.push(result);
        } catch {
          toast(t(`${DRYBULK_CARGO_OPS_LANG_BASE}.documents.upload-failed`));
        } finally {
          setStaging((n) => n - 1);
        }
      }),
    );
    return done;
  };

  const dropFiles = async (files: File[]) => {
    const dropKey = key;
    const result = await stageFiles(files);
    result.forEach((item) => store.getState().addStaged(dropKey, item));
  };

  const removeStaged = (documentUrl: string) => {
    heldFiles.current.delete(documentUrl);
    store.getState().removeStaged(key, documentUrl);
  };

  const invalidate = (...keys: (readonly unknown[])[]) =>
    keys.forEach((queryKey) => void queryClient.invalidateQueries({ queryKey }));

  const keys = {
    messages: DRYBULK_CARGO_OPS_QUERY_KEYS.messages(operationId, threadKey ?? ""),
    files: DRYBULK_CARGO_OPS_QUERY_KEYS.messageAttachments(operationId, threadKey ?? ""),
    detail: DRYBULK_CARGO_OPS_QUERY_KEYS.detail(operationId),
  };

  const clearPending = (messageId: string) =>
    store.getState().setPendingAttach(key, messageId, null);

  const handleAttachFailure = async (error: unknown, messageId: string, allHeld: boolean) => {
    const status = problemStatus(error);
    const serverText = messageErrorText(error);
    if (status === 409) {
      const files = await queryClient
        .fetchQuery({
          queryKey: keys.files,
          queryFn: () =>
            drybulkCargoOperationsService.listMessageAttachments(operationId, threadKey),
          staleTime: 0,
        })
        .catch(() => null);
      if (!files?.some((file) => file.messageId === messageId)) {
        toast(serverText ?? t(`${L}.attach-failed`));
      }
      clearPending(messageId);
      invalidate(keys.files, keys.detail);
      return;
    }
    if (status === 403 || status === 404) {
      toast(serverText ?? t(`${L}.attach-failed`));
      clearPending(messageId);
      return;
    }
    if (status === 400 && !allHeld) {
      const current = store.getState().drafts[key]?.pendingAttach[messageId];
      if (current) {
        store.getState().setPendingAttach(key, messageId, { ...current, needsRepick: true });
      }
      return;
    }
    toast(problemMessage(error) ?? t(`${L}.attach-failed`));
  };

  const attach = async (messageId: string, files: MessageFile[], allHeld: boolean) => {
    try {
      await runTransaction(telemetry("attach_cargo_operation_message_files"), (transactionId) =>
        drybulkCargoOperationsService.attachMessageFiles(
          operationId,
          { messageId, threadKey: threadKey ?? "", files },
          { transactionId },
        ),
      );
      clearPending(messageId);
      invalidate(keys.files, keys.messages, keys.detail);
    } catch (error) {
      await handleAttachFailure(error, messageId, allHeld);
    }
  };

  const send = async () => {
    if (!canSend || !threadKey || !role) return;
    const body = text.trim() !== "" ? text : t(`${L}.files-only-body`, { count: staged.length });
    setSending(true);
    setInlineError(null);
    try {
      let messageId: string;
      try {
        const created = await runTransaction(
          telemetry("send_cargo_operation_message"),
          (transactionId) =>
            sendMessage({
              payload: {
                entityId: operationId,
                entityType: MESSAGE_ENTITY_TYPE,
                threadKey,
                message: body,
                sender: user?.preferredUsername ?? "",
                sentAt: new Date().toISOString(),
                secondRelatedEntityId: vesselCallId ?? undefined,
                notificationFor: messageNotificationTypes(role),
              },
              transactionId,
            }),
        );
        messageId = created.id;
      } catch (error) {
        if (problemStatus(error) === 400) {
          setInlineError(messageErrorText(error) ?? t(`${L}.send-rejected`));
        } else {
          toast(problemMessage(error) ?? t(`${L}.send-failed`));
        }
        return;
      }
      const files = staged.map(toMessageFile);
      store
        .getState()
        .completeSend(key, messageId, files.length ? { files, needsRepick: false } : null);
      invalidate(keys.messages);
      if (files.length) {
        await attach(
          messageId,
          files,
          files.every((file) => heldFiles.current.has(file.documentUrl)),
        );
      }
    } finally {
      setSending(false);
    }
  };

  const retry = async (messageId: string) => {
    const pending = store.getState().drafts[key]?.pendingAttach[messageId];
    if (!pending || busy) return;
    setRetryingId(messageId);
    try {
      let allHeld = true;
      const restaged = await Promise.all(
        pending.files.map(async (file) => {
          const held = heldFiles.current.get(file.documentUrl);
          if (!held) {
            allHeld = false;
            return file;
          }
          const result = await drybulkCargoOperationsService.stageAttachment(held, "");
          heldFiles.current.set(result.documentUrl, held);
          return toMessageFile(result);
        }),
      ).catch(() => null);
      if (!restaged) {
        toast(t(`${DRYBULK_CARGO_OPS_LANG_BASE}.documents.upload-failed`));
        return;
      }
      const files = restaged;
      store.getState().setPendingAttach(key, messageId, { files, needsRepick: false });
      await attach(messageId, files, allHeld);
    } finally {
      setRetryingId(null);
    }
  };

  const repick = async (messageId: string, picked: File[]) => {
    setRetryingId(messageId);
    try {
      const result = await stageFiles(picked);
      if (result.length === 0) return;
      const files = result.map(toMessageFile);
      store.getState().setPendingAttach(key, messageId, { files, needsRepick: false });
      await attach(messageId, files, true);
    } finally {
      setRetryingId(null);
    }
  };

  return {
    text,
    staged,
    pendingAttach,
    sending,
    staging,
    retryingId,
    busy,
    canSend,
    inlineError,
    setText: (value: string) => {
      if (inlineError) setInlineError(null);
      store.getState().setText(key, value);
    },
    dropFiles,
    removeStaged,
    send,
    retry,
    repick,
  };
};

export type CargoMessageController = ReturnType<typeof useSendCargoMessage>;
